import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success(`Welcome back, ${user.name}`);
      if (user.role === "admin") navigate("/admin");
      else navigate(location.state?.from || "/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-5rem)] grid lg:grid-cols-2" data-testid="login-page">
      <div className="hidden lg:block relative bg-obsidian">
        <img src="https://images.unsplash.com/photo-1616837874254-8d5aaa63e273?crop=entropy&cs=srgb&fm=jpg&q=85" alt="" className="w-full h-full object-cover opacity-50" />
        <div className="absolute inset-0 flex items-end p-12">
          <div>
            <img src="/logo.png" alt="ChainSmart" className="h-16  mb-6" />
            <h2 className="font-serif text-4xl text-white leading-tight max-w-sm">Sign in to continue your jewellery journey</h2>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          <p className="overline text-gold-dark mb-2">Welcome Back</p>
          <h1 className="font-serif text-4xl text-obsidian mb-8">Login</h1>

          {error && <div className="bg-discount/10 border border-discount/30 text-discount text-sm px-4 py-3 mb-5 rounded-sm" data-testid="login-error">{error}</div>}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1.5 rounded-sm" data-testid="login-email" placeholder="you@example.com" />
            </div>
            <div>
              <div className="flex justify-between items-center">
                <Label htmlFor="password">Password</Label>
                <ForgotPassword />
              </div>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="mt-1.5 rounded-sm" data-testid="login-password" placeholder="••••••••" />
            </div>
            <button type="submit" disabled={loading} className="w-full inline-flex items-center justify-center gap-2 bg-obsidian text-white font-semibold py-3.5 hover:bg-gold hover:text-obsidian transition-colors disabled:opacity-50 rounded-sm" data-testid="login-submit">
              {loading && <Loader2 className="w-4 h-4 animate-spin" />} Sign In
            </button>
          </form>

          <p className="text-sm text-muted-foreground mt-6">
            Don't have an account?{" "}
            <Link to="/register" className="text-gold-dark font-semibold hover:underline" data-testid="go-register">Create one</Link>
          </p>
        </div>
      </div>
    </div>
  );
}

function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch {
      setSent(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        <button type="button" className="text-xs text-gold-dark hover:underline" data-testid="forgot-password-link">Forgot?</button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-serif text-2xl">Reset Password</DialogTitle></DialogHeader>
        {sent ? (
          <p className="text-sm text-muted-foreground py-4">If an account exists for that email, a reset link has been generated. (Check server logs in this demo environment.)</p>
        ) : (
          <form onSubmit={submit} className="space-y-4 pt-2">
            <Input type="email" placeholder="Your email" value={email} onChange={(e) => setEmail(e.target.value)} required className="rounded-sm" data-testid="forgot-email" />
            <button type="submit" disabled={loading} className="w-full bg-obsidian text-white py-3 font-semibold hover:bg-gold hover:text-obsidian transition-colors rounded-sm" data-testid="forgot-submit">Send Reset Link</button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
